import React from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  Sparkles,
  Landmark,
  CloudUpload,
  BarChart3,
  TrendingUp,
  ShieldCheck,
  Lock,
  Workflow,
  Code2,
  ClipboardList,
  Bot,
  Users,
  PieChart,
  FileLock2,
  FileEdit,
  ArrowLeftRight,
  ArrowUpRight,
  ScrollText,
} from "lucide-react";

const COMPETENCIES = [
  { icon: Activity, label: "Healthcare SaaS" },
  { icon: Sparkles, label: "AI Automation" },
  { icon: Landmark, label: "Medicaid Technology" },
  { icon: CloudUpload, label: "Cloud Migration" },
  { icon: BarChart3, label: "Data Analytics" },
  { icon: TrendingUp, label: "Business Intelligence" },
  { icon: ShieldCheck, label: "HIPAA Solutions" },
  { icon: Lock, label: "Cybersecurity" },
  { icon: Workflow, label: "Workflow Automation" },
  { icon: Code2, label: "Application Development" },
];

const PLATFORMS = [
  {
    id: "P01",
    slug: "medicaid-case-management",
    icon: ClipboardList,
    name: "Medicaid Case Management Platform",
    tag: "Case Ops",
    description:
      "Case intake, eligibility, adjudication, and appeals in a HIPAA-aligned workspace built for state Medicaid programs.",
    bullets: [
      "FHIR R4 record model",
      "Worker queue routing",
      "Audit-ready trail",
    ],
  },
  {
    id: "P02",
    slug: "ai-intake-automation",
    icon: Bot,
    name: "AI Intake Automation",
    tag: "AI · Ingestion",
    description:
      "Structured LLM-powered intake for forms, phone, and web channels — reducing manual data entry with human-in-loop review.",
    bullets: [
      "PII redaction pipeline",
      "70%+ manual entry reduction",
      "Downstream case routing",
    ],
  },
  {
    id: "P03",
    slug: "provider-portal",
    icon: Users,
    name: "Provider Portal",
    tag: "Self-service",
    description:
      "Portal for Medicaid providers — enrollment, credentialing, claims status, and prior authorization on one SSO surface.",
    bullets: [
      "Federated identity",
      "Credentialing workflow",
      "Prior-auth submissions",
    ],
  },
  {
    id: "P04",
    slug: "claims-analytics",
    icon: PieChart,
    name: "Claims Analytics",
    tag: "BI · FWA",
    description:
      "Real-time claims dashboards with fraud/waste/abuse anomaly detection and provider network utilization insight.",
    bullets: [
      "Governed lakehouse",
      "FWA anomaly detection",
      "Utilization scorecards",
    ],
  },
  {
    id: "P05",
    slug: "healthcare-workflow-automation",
    icon: Workflow,
    name: "Healthcare Workflow Automation",
    tag: "Automation",
    description:
      "Bots and workflow engines that automate insurance verification, referral routing, and clinical documentation tasks.",
    bullets: [
      "HIPAA-aligned by construction",
      "Referral + eligibility bots",
      "Human-in-loop gates",
    ],
  },
  {
    id: "P06",
    slug: "hipaa-document-management",
    icon: FileLock2,
    name: "HIPAA Document Management",
    tag: "Records",
    description:
      "Encrypted document store with retention policies, e-signature, chain-of-custody, and DTI-approved classification tagging.",
    bullets: [
      "Immutable audit logs",
      "Retention rulesets",
      "E-signature + BAA",
    ],
  },
  {
    id: "P07",
    slug: "digital-forms",
    icon: FileEdit,
    name: "Digital Forms Platform",
    tag: "No-code",
    description:
      "Drag-and-drop no-code form builder for public sector programs with WCAG 2.2 AA accessibility and multi-language delivery.",
    bullets: [
      "WCAG 2.2 AA accessibility",
      "Multi-language runtime",
      "E-signature built-in",
    ],
  },
  {
    id: "P08",
    slug: "secure-file-exchange",
    icon: ArrowLeftRight,
    name: "Secure File Exchange",
    tag: "Integration",
    description:
      "SFTP + AS2 gateway with encryption-at-rest, key rotation, HIPAA BAA controls, and immutable audit logs.",
    bullets: [
      "SFTP · AS2 · MFT",
      "X12 / EDI / NCPDP",
      "Immutable transfer logs",
    ],
  },
];

export default function Portfolio({ SectionHeader }) {
  return (
    <section id="portfolio" className="relative py-24 md:py-32 px-6 md:px-10 border-t border-slate-800/50">
      <div className="max-w-[1400px] mx-auto">
        <SectionHeader
          index="02"
          kicker="Portfolio"
          title="Ten competencies. Eight platforms you can procure."
          description="We do not sell hours &mdash; we deliver named platforms with acceptance criteria, control mappings, and defined integration seams. Deployed across health, public safety, judiciary, revenue, human services, education, and defense workloads. Below: the competencies we operate in, the productized platforms we ship, and the compliance envelope every engagement runs inside."
        />

        {/* Core Competencies */}
        <div className="reveal">
          <div className="flex items-center gap-4 mb-6">
            <span className="eyebrow">Core Competencies</span>
            <div className="h-px flex-1 bg-slate-800" />
            <span className="font-mono text-[10px] tracking-widest text-slate-500 uppercase">10 practice areas</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-slate-800/60 border border-slate-800/60">
            {COMPETENCIES.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.label}
                  className="bg-slate-950 hover:bg-slate-900/70 transition-colors p-5 flex flex-col items-start gap-3 group"
                >
                  <Icon size={20} strokeWidth={1.4} className="text-cyan-300 group-hover:text-cyan-200 transition-colors" />
                  <span className="text-sm font-medium text-slate-100 tracking-tight leading-snug">{c.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Packaged Platforms */}
        <div className="mt-16 reveal">
          <div className="flex items-center gap-4 mb-6">
            <span className="eyebrow">Packaged Platforms</span>
            <div className="h-px flex-1 bg-slate-800" />
            <span className="font-mono text-[10px] tracking-widest text-slate-500 uppercase">
              {PLATFORMS.length} productized deliverables
            </span>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PLATFORMS.map((p) => {
              const Icon = p.icon;
              return (
                <Link
                  to={`/platforms/${p.slug}`}
                  key={p.id}
                  className="panel hover-lift p-6 flex flex-col group cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-10 h-10 rounded-md bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center">
                      <Icon size={18} strokeWidth={1.5} className="text-cyan-300" />
                    </div>
                    <span className="font-mono text-[10px] tracking-widest text-slate-500">{p.id}</span>
                  </div>
                  <div className="eyebrow-muted mb-2">{p.tag}</div>
                  <h3 className="text-base font-semibold text-slate-50 tracking-tight leading-snug mb-3 group-hover:text-cyan-200 transition-colors">{p.name}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed mb-4">{p.description}</p>
                  <ul className="space-y-1.5 mb-5">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-[11px] text-slate-300 leading-relaxed">
                        <span className="mt-1 h-1 w-1 rounded-full bg-cyan-400 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <span className="mt-auto inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300 link-underline w-fit">
                    Explore platform <ArrowUpRight size={11} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Compliance & Integration Envelope */}
        <div className="mt-16 reveal">
          <div className="panel corner-brackets p-8 md:p-10">
            <div className="grid md:grid-cols-12 gap-8 items-start">
              <div className="md:col-span-4">
                <div className="w-12 h-12 rounded-md bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center mb-5">
                  <ScrollText size={20} strokeWidth={1.4} className="text-cyan-300" />
                </div>
                <span className="eyebrow">Compliance &amp; Integration</span>
                <h3 className="mt-2 text-2xl font-semibold text-slate-50 tracking-tight leading-snug">
                  Every platform ships inside a defensible envelope.
                </h3>
              </div>
              <div className="md:col-span-8 space-y-5 text-sm text-slate-300 leading-relaxed">
                <p>
                  Delivery is calibrated to <span className="text-slate-100 font-medium">State of Delaware DTI technology standards</span>{" "}
                  &mdash; including architecture review, data classification, and hosting posture requirements &mdash; so acceptance is
                  routine, not remedial. The envelope is applied to every engagement, health or otherwise.
                </p>
                <p>
                  Sector-specific controls layer in as required: <span className="text-slate-100 font-medium">HIPAA / HITECH</span> with Business
                  Associate Agreements for healthcare;{" "}
                  <span className="text-slate-100 font-medium">CJIS Security Policy</span> for law-enforcement workloads;{" "}
                  <span className="text-slate-100 font-medium">FERPA</span> for education programs;{" "}
                  <span className="text-slate-100 font-medium">CMMC 2.0 &amp; DFARS</span> for defense contract vehicles; and{" "}
                  <span className="text-slate-100 font-medium">42 CFR Part 2</span> where substance-use records are in scope.
                </p>
                <p>
                  Integrations across state HIEs, MCOs, RMV/DMV systems, court case managers, LEA / CJIS interfaces, LMS platforms, and CMS
                  endpoints are delivered over <span className="text-slate-100 font-medium">SFTP, AS2, and managed file transfer</span>{" "}
                  channels &mdash; encoded in <span className="text-slate-100 font-medium">FHIR R4, X12, NCPDP, NIEM, and Ed-Fi</span>{" "}
                  where applicable &mdash; with signed data-sharing agreements, encryption in transit and at rest, and immutable transfer logs.
                </p>
                <div className="pt-4 flex flex-wrap gap-2">
                  {[
                    "DE DTI Standards",
                    "NIST 800-53 Rev.5",
                    "CJIS Security Policy",
                    "FERPA",
                    "HIPAA BAA",
                    "42 CFR Part 2",
                    "CMMC 2.0 · DFARS",
                    "StateRAMP",
                    "FHIR R4",
                    "X12 EDI",
                    "NIEM",
                    "Ed-Fi",
                    "NCPDP",
                    "AS2 · SFTP",
                  ].map((t) => (
                    <span
                      key={t}
                      className="font-mono text-[10px] tracking-widest uppercase text-slate-300 border border-slate-800 px-2.5 py-1.5"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
